import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl, FormattedMessage } from 'react-intl'
import { AlertBox, Icons, Button, Field, Spinner } from '../../../index'
import { useCommerceSFRAAPI, useConfig, useCookie } from 'msp-integrations'
import { NewsletterFormProps } from './NewsletterForm.types'
import getNewsletterFields from './NewsletterForm.fields'

const NewsletterForm = ({
  id,
  className,
  buttonClassname = '',
  onSuccess,
  inFlyout,
  isDarkMode,
  noDescription,
  newsletterSource
}: NewsletterFormProps) => {
  const { eventTracking } = useCookie()
  const form = useForm()
  const SFRAClient = useCommerceSFRAAPI()
  const { formatMessage } = useIntl()
  const {
    title,
    API_ERROR_MESSAGE,
    theme: {
      font: { regular: font },
      mspComponents: { newsletterForm }
    }
  } = useConfig()
  const fieldConfig = getNewsletterFields(id || '')
  const alertBoxRef = useRef<typeof AlertBox>()
  const [alertMessage, setAlertMessage] = useState<string>('')

  const submitForm = async (data: {
    email: string
    firstName: string
    lastName: string
  }) => {
    try {
      const response = await SFRAClient.Auth.submitGuestRegistration({
        body: {
          ...data,
          newsletter: data[`newsletter_${id}`],
          newsletterSource
        }
      })

      if (response.error) {
        setAlertMessage(response?.error.message)
        alertBoxRef?.current?.showAlert()
      } else {
        eventTracking('newsletter')
        onSuccess && onSuccess()
      }
    } catch (error) {
      setAlertMessage(formatMessage(API_ERROR_MESSAGE))
      alertBoxRef?.current?.showAlert()
    }
  }

  return (
    <div
      key={id || null}
      data-testid={'newsletter-form'}
      className={`flex flex-col ${className}`}
    >
      <AlertBox
        ref={alertBoxRef}
        type={'error'}
        title={alertMessage}
        autoCloseTime={4000}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      />
      <form
        onSubmit={form.handleSubmit(submitForm)}
        id={id || 'login-form'}
        data-testid={'login-form-test'}
      >
        {!noDescription && (
          <div
            className={`${font} text-16 ${newsletterForm} ${
              inFlyout ? 'mb-6' : 'mb-8'
            }`}
          >
            <FormattedMessage
              defaultMessage='Subscribe to the <b>{title} newsletter</b> now and be the first to receive <b>exclusive offers and information</b> about new teas.'
              id='msp.newsletter_form.form_description'
              values={{
                b: (chunks: string) => <b>{chunks}</b>,
                title: title?.toUpperCase() + ' ' || ''
              }}
            />
          </div>
        )}

        <div className='mb-6 flex w-full space-x-5 md:space-x-7'>
          <Field
            className={'flex-1'}
            control={form.control}
            config={fieldConfig.firstName}
            error={form.errors?.firstName}
            isDisabled={form.formState.isSubmitting}
            additionalProps={{ layout: 'underline', isDarkMode: isDarkMode }}
          />

          <Field
            className={'flex-1'}
            control={form.control}
            config={fieldConfig.lastName}
            error={form.errors?.lastName}
            isDisabled={form.formState.isSubmitting}
            additionalProps={{ layout: 'underline', isDarkMode: isDarkMode }}
          />
        </div>

        <Field
          className={'w-full'}
          control={form.control}
          config={fieldConfig.email}
          error={form.errors?.email}
          isDisabled={form.formState.isSubmitting}
          additionalProps={{ layout: 'underline', isDarkMode: isDarkMode }}
        />
        <p
          className={`${font.regular} tracking-0 text-14 pt-6 ${
            isDarkMode ? 'text-white' : 'text-menuGray'
          }`}
        >
          {formatMessage({
            id: 'msp.newsletter_form.required',
            defaultMessage: '* Required field'
          })}
        </p>

        <Button
          className={`mb-6 mt-11 w-auto md:mt-8 ${buttonClassname}`}
          onClick={() => form.clearErrors(Object.keys(fieldConfig))}
          iconName={!form.formState.isSubmitting ? 'ArrowRight' : ''}
          iconPosition='right'
          isDisabled={form.formState.isSubmitting}
          isLoading={form.formState.isSubmitting}
          isSubmit
          loadingContent={<Spinner size='xl' />}
          isDarkMode={isDarkMode}
        >
          {formatMessage({
            defaultMessage: 'Subscribe',
            id: 'msp.newsletter_form.button.subscribe'
          })}
        </Button>

        <Field
          className={'text-12 tracking[0.45px]'}
          control={form.control}
          config={fieldConfig[`newsletter_${id}`]}
          error={form.errors ? form.errors[`newsletter_${id}`] : null}
          isDisabled={form.formState.isSubmitting}
          additionalProps={{ isDarkMode: isDarkMode }}
        />
      </form>
    </div>
  )
}

export default NewsletterForm
